import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import { formatDate } from '../../utils/Utils';
import useDeepLinkParams from '../../hooks/useDeepLinkParams';
import { useMyCalibration, CALIBRATION_STATUS_COLORS, StatusBadge } from './config';
import { GoalResultsSection, FeedbackSummarySection } from './CalibrationFindings';

// PRD PM §5 (Task 30) — çalışan kalibrasyon el sıkışma ekranı: kalibrasyon
// kaydım (durum) + bulgular (izinli veriler: kendi 360 özetim + hedef
// sonuçlarım) + "Onaylıyorum (El Sıkışma)" + açılır itiraz notu alanı.
// MyPeriodResultsPage'in (Task 29) sayfa desenini izler.
//
// Gelen Kutum derin bağlantısı (2026-10-28): ?record= ile gelinince o kayıt
// seçilir — paralel dönemlerde (PMF-45) kart, tıklanan dönemin el sıkışmasını
// açar (varsayılan "ilk açık kayıt" başka döneme düşebilirdi).
const Container = styled.div``;

// Bu sayfa (self-servis, Card, DataTable YOK) MyBenefitsPage'in Title deseniyle
// aynı — PageHeader'ın eski flex/space-between'i tek çocukla hiçbir şey
// yapmıyordu (ölü CSS), düz bir blok yeterli.
const PageHeader = styled.div`
  margin-bottom: 18px;
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 20px 22px;
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 24px;
  text-align: center;
`;

const EmptyText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  max-width: 420px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const PeriodName = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Section = styled.div`
  margin-bottom: 22px;
  padding-top: 18px;
  border-top: 1px solid #E8ECF0;
`;

const MetaRow = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 4px;
`;

const InfoBanner = styled.div`
  background: #EFF6FF;
  border: 1px solid #DBEAFE;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const InfoBannerRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const InfoBannerCloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.dark.little};
  cursor: pointer;
  padding: 0;
  line-height: 1;
  font-size: ${({ theme }) => theme.metrics.font.size.md};
`;

const ToggleLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary.main};
  cursor: pointer;
  padding: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  text-decoration: underline;
  align-self: flex-start;
  margin-bottom: 12px;
`;

const ActionCol = styled.div`
  display: flex;
  flex-direction: column;
`;

// PMF-45 — birden fazla AÇIK kalibrasyon kaydı (paralel yıllık + çeyreklik
// dönem) olduğunda dönem seçici. Tek kayıtlı kiracıda hiç render edilmez.
const RecordTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
`;

const RecordTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  background: ${({ $active }) => ($active ? '#EFF6FF' : '#fff')};
  border: 1px solid ${({ $active }) => ($active ? '#BFDBFE' : '#E8ECF0')};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme, $active }) => ($active ? theme.metrics.font.weight.semiBold : theme.metrics.font.weight.regular)};
`;

export default function MyCalibrationPage() {
  const { t, i18n } = useTranslation();
  const {
    current, pendingRecords, selectRecord,
    fetching, refresh, approve, getMyDetail, getMyFeedbackSummary, getMyGoals,
  } = useMyCalibration();
  const { values: { record: linkedRecordId } } = useDeepLinkParams(['record']);

  // Seçim kimlikle tutulur (useMyCalibration) — kayıt listesi gelmeden de verilebilir; listede yoksa varsayılana düşer.
  useEffect(() => {
    if (linkedRecordId) selectRecord(linkedRecordId);
  }, [linkedRecordId, selectRecord]);

  const [goals, setGoals] = useState([]);
  const [feedbackSummary, setFeedbackSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [findingsLoading, setFindingsLoading] = useState(false);

  const [showObjection, setShowObjection] = useState(false);
  const [objectionNote, setObjectionNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [objectionInfoShown, setObjectionInfoShown] = useState(false);

  // İnceleme bulgusu (Important) düzeltmesi — bu, yalnız kaydın kendisi
  // DEĞİŞTİĞİNDE (farklı recordId — ör. sayfa ilk yüklendiğinde ya da başka
  // bir döneme geçildiğinde) sıfırlanmalı. Önceden `current` referansının HER
  // değişiminde (handleApprove sonrası refresh() de dahil) çalışıyordu; bu,
  // onay çağrısı BAŞARILI olup itiraz notu gönderildiğinde bile
  // objectionInfoShown'ı bir network round-trip içinde false'a geri
  // döndürüyor, "süreç durmaz" banner'ının aynı kayıt üzerinde anında
  // kaybolmasına yol açıyordu (durum invited→employee_approved geçişi ile
  // `current` yeni bir obje referansı alıyor ama recordId AYNI kalıyor).
  const lastRecordIdRef = useRef(null);
  useEffect(() => {
    const recordId = current?.recordId ?? null;
    if (lastRecordIdRef.current === recordId) return;
    lastRecordIdRef.current = recordId;
    setShowObjection(false);
    setObjectionNote('');
    setObjectionInfoShown(false);
  }, [current]);

  // Bulgular (hedefler/360 özeti/detay) — AYNI kayıt için bile durum
  // değiştiğinde (ör. employee_approved→manager_approved) yeniden çekilmeli,
  // bu yüzden `current` referans değişiminde (refresh() dahil) HER ZAMAN
  // çalışır — yukarıdaki editör/banner state'inin sıfırlanmasından bilerek
  // AYRI bir effect'e taşındı.
  useEffect(() => {
    if (!current) {
      setGoals([]);
      setFeedbackSummary(null);
      setDetail(null);
      return;
    }

    const isManagerApproved = current.status === 'manager_approved';
    setFindingsLoading(true);
    Promise.all([
      getMyGoals(current.periodId),
      isManagerApproved ? getMyFeedbackSummary(current.periodId) : Promise.resolve(null),
      isManagerApproved ? getMyDetail(current.recordId) : Promise.resolve(null),
    ]).then(([gl, fb, dt]) => {
      setGoals(gl || []);
      setFeedbackSummary(fb);
      setDetail(dt);
    }).finally(() => setFindingsLoading(false));
  }, [current, getMyGoals, getMyFeedbackSummary, getMyDetail]);

  const handleApprove = useCallback(async () => {
    if (!current) return;
    setSubmitting(true);
    const noteToSend = showObjection ? objectionNote.trim() : '';
    // Analitik Plan 5 — okunan sürüm gönderilir; reddedilirse (409 versionConflict) liste yenilenir.
    const ok = await approve(current.recordId, noteToSend || undefined, current.version);
    setSubmitting(false);
    if (ok && noteToSend) setObjectionInfoShown(true);
    refresh();
  }, [current, showObjection, objectionNote, approve, refresh]);

  const cfg = current ? (CALIBRATION_STATUS_COLORS[current.status] || CALIBRATION_STATUS_COLORS.invited) : null;

  return (
    <Container>
      <PageHeader>
        <PageTitle>{t('performance.calibration.myTitle')}</PageTitle>
      </PageHeader>

      {fetching && (
        <Card><Centered><EmptyText>{t('common.loading')}</EmptyText></Centered></Card>
      )}

      {!fetching && !current && (
        <Card><Centered><EmptyText>{t('performance.calibration.myEmpty')}</EmptyText></Centered></Card>
      )}

      {/* PMF-45 — paralel dönemlerde ikinci kalibrasyon daveti eskiden HİÇ
          görünmüyordu (hook yalnız ilk açık kaydı döndürüyordu); çalışan o
          dönemi el sıkışamadığı için dönem kapanamıyordu. Seçici yalnız
          BİRDEN FAZLA açık kayıt varsa çıkar. */}
      {!fetching && pendingRecords.length > 1 && (
        <RecordTabs>
          {pendingRecords.map((r) => {
            const rc = CALIBRATION_STATUS_COLORS[r.status] || CALIBRATION_STATUS_COLORS.invited;
            return (
              <RecordTab
                key={r.recordId}
                type="button"
                $active={current?.recordId === r.recordId}
                onClick={() => selectRecord(r.recordId)}
              >
                {r.periodName || '-'}
                <StatusBadge color={rc.color} bg={rc.bg}>
                  {t(`performance.calibration.status.${r.status}`, { defaultValue: r.status })}
                </StatusBadge>
              </RecordTab>
            );
          })}
        </RecordTabs>
      )}

      {!fetching && current && (
        <Card>
          <InfoRow>
            <PeriodName>{current.periodName || '-'}</PeriodName>
            {cfg && (
              <StatusBadge color={cfg.color} bg={cfg.bg}>
                {t(`performance.calibration.status.${current.status}`, { defaultValue: current.status })}
              </StatusBadge>
            )}
          </InfoRow>

          {/* İnceleme bulgusu (Important) düzeltmesi — banner artık durum
              kapısından (eskiden yalnız status==='invited' bloğunun içinde,
              onay sonrası durum employee_approved'a geçince birlikte
              unmount oluyordu) BAĞIMSIZ, yalnız objectionInfoShown state'ine
              bağlı. Kullanıcı × ile kapatana ya da sayfadan ayrılana (unmount)
              dek kalıcı görünür. Yalnız itiraz notu GÖNDERİLMİŞSE görünür
              (bkz. handleApprove — noteToSend boşsa hiç true olmuyor). */}
          {objectionInfoShown && (
            <InfoBanner>
              <InfoBannerRow>
                <span>{t('performance.calibration.objectionInfo')}</span>
                <InfoBannerCloseButton
                  type="button"
                  onClick={() => setObjectionInfoShown(false)}
                  aria-label={t('common.close')}
                >
                  ×
                </InfoBannerCloseButton>
              </InfoBannerRow>
            </InfoBanner>
          )}

          {findingsLoading ? (
            <EmptyText>{t('common.loading')}</EmptyText>
          ) : (
            <>
              <Section>
                <GoalResultsSection goals={goals} t={t} />
              </Section>

              <Section>
                {current.status === 'manager_approved' ? (
                  <FeedbackSummarySection summary={feedbackSummary} showComments={false} t={t} />
                ) : (
                  // İnceleme bulgusu (Minor 2) düzeltmesi — bu metin,
                  // getMyFeedbackSummary/getMyDetail'in AYNI koşulda (status
                  // !== manager_approved) atlandığı skipErrorToast dalını
                  // temsil ediyor. Bu sayfanın kendi self-servis "employee-view"
                  // ucu (GET v1/calibrations/my/{id}, bkz. config'teki getMyDetail)
                  // tam olarak bu durumda BE'den 409 "performance.calibration.notYetVisible"
                  // döner (CalibrationDataAccess.GetEmployeeViewAsync) — sayfanın
                  // KENDİ kaydının onay durumuna bağlı gizliliği bu anahtarla
                  // ifade edilir; feedback.notYetVisible ayrı bir uca
                  // (FeedbackResponseController.GetMySummary) ait, burada ödünç
                  // alınmamalı.
                  <EmptyText>{t('responseErrors.performance.calibration.notYetVisible')}</EmptyText>
                )}
              </Section>

              {detail && (detail.objectionNote || detail.employeeApprovedAt || detail.managerApprovedAt) && (
                <Section>
                  {detail.employeeApprovedAt && (
                    <MetaRow>{t('performance.calibration.employeeApprovedAt')}: {formatDate(detail.employeeApprovedAt, i18n.language)}</MetaRow>
                  )}
                  {detail.managerApprovedAt && (
                    <MetaRow>{t('performance.calibration.managerApprovedAt')}: {formatDate(detail.managerApprovedAt, i18n.language)}</MetaRow>
                  )}
                  {detail.objectionNote && (
                    <MetaRow><strong>{t('performance.calibration.objectionNote')}:</strong> {detail.objectionNote}</MetaRow>
                  )}
                </Section>
              )}
            </>
          )}

          {current.status === 'invited' && (
            <Section>
              <ActionCol>
                {!showObjection && (
                  <ToggleLink type="button" onClick={() => setShowObjection(true)}>
                    {t('performance.calibration.objectionToggle')}
                  </ToggleLink>
                )}
                {showObjection && (
                  <div style={{ marginBottom: 12 }}>
                    <TextInput
                      label={t('performance.calibration.objectionNote')}
                      type="textarea"
                      rows={3}
                      value={objectionNote}
                      onChange={(e) => setObjectionNote(e.target.value)}
                    />
                  </div>
                )}
                <div>
                  <Button onClick={handleApprove} disabled={submitting} loading={submitting}>
                    {t('performance.calibration.confirmHandshake')}
                  </Button>
                </div>
              </ActionCol>
            </Section>
          )}

          {current.status === 'employee_approved' && (
            <Section>
              <EmptyText>{t('performance.calibration.waitingManager')}</EmptyText>
            </Section>
          )}
        </Card>
      )}
    </Container>
  );
}
